import { type ReactNode, useCallback, useMemo, useRef, type ReactElement } from 'react'
import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs'
import { isDefined, isDeleted, isNotDefined, isNotEmpty, type AppId, type AppVersionData } from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { modals } from '@mantine/modals'
import { useDisclosure } from '@mantine/hooks'
import { Stack } from '@mantine/core'
import { translateLabels, useConnectApp, useProfile, useTranslation } from '@front/model'
import { ButtonAdd, NotReady, Page, TAlert, TSegmentedControl, type TSegmentedControlOption } from '@components'
import {
  CONNECT_DEVELOPER_HREF,
  isDraftVersion,
  isPublishedVersion,
  parseConnectAppId,
  sortVersions,
} from '../integration/util'
import { ConnectAppLogo } from '../integration/ui'
import { ConnectAccessGuard } from '../integration/access'
import { MonitoringTab } from './components/monitoring/MonitoringTab'
import { StartDraftModal } from './components/draft'
import { ConfigTabBody } from './components/ConfigTabBody'
import { AppDetailsTab } from './components/AppDetailsTab'

const CONNECT_EDITOR_SECTIONS = ['details', 'config', 'monitoring'] as const

type ConnectEditorSection = (typeof CONNECT_EDITOR_SECTIONS)[number]

const SEGMENTED_CONTROL_OPTIONS: TSegmentedControlOption<ConnectEditorSection>[] = [
  { value: 'details', label: 'Details', icon: 'edit' },
  { value: 'config', label: 'Config', icon: 'deployed_code' },
  { value: 'monitoring', label: 'Monitoring', icon: 'monitoring' },
]

// Monitoring shows data across every company, so only Terros (tantalim) users get the tab.
function getSectionOptions(isTantalim: boolean): TSegmentedControlOption<ConnectEditorSection>[] {
  if (isTantalim) return SEGMENTED_CONTROL_OPTIONS
  return SEGMENTED_CONTROL_OPTIONS.filter((option) => option.value !== 'monitoring')
}

function resolveSection(section: ConnectEditorSection, isTantalim: boolean): ConnectEditorSection {
  if (section === 'monitoring' && !isTantalim) return 'config'
  return section
}

function isConnectEditorSection(value: unknown): value is ConnectEditorSection {
  return CONNECT_EDITOR_SECTIONS.some((section) => section === value)
}

function ConnectAppPage(): ReactElement {
  const { appId } = Route.useParams()
  const parsedAppId = parseConnectAppId(appId)

  if (!parsedAppId) {
    return (
      <ConnectAccessGuard>
        <NotReady error='Invalid app id' id='connect app' />
      </ConnectAccessGuard>
    )
  }

  return <AppEditor appId={parsedAppId} />
}

type Props = {
  appId: AppId
}

export function AppEditor({ appId }: Props): ReactNode {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [urlVersion, setUrlVersion] = useQueryState('version', parseAsString)
  const [rawSection, setSection] = useQueryState(
    'section',
    parseAsStringEnum<ConnectEditorSection>([...CONNECT_EDITOR_SECTIONS]).withDefault('config')
  )
  const [startDraftOpened, { open: openStartDraft, close: closeStartDraft }] = useDisclosure(false)
  const { isTantalim } = useProfile()
  const section = resolveSection(rawSection, isTantalim)
  const segmentedControlOptions = useMemo(() => translateLabels(getSectionOptions(isTantalim), t), [isTantalim, t])
  const isFormDirty = useRef<(() => boolean) | undefined>(undefined)
  const registerDirtyCheck = useCallback((check: (() => boolean) | undefined): void => {
    isFormDirty.current = check
  }, [])

  function confirmLeave(onConfirm: () => void): void {
    if (!isFormDirty.current?.()) {
      onConfirm()
      return
    }
    modals.openConfirmModal({
      centered: true,
      title: t('Unsaved Changes'),
      children: t('You have unsaved changes. Are you sure you want to leave?'),
      labels: { cancel: t('Stay on Page'), confirm: t('Discard Changes') },
      confirmProps: { color: 'red' },
      onConfirm,
    })
  }

  const appState = useConnectApp(appId)
  const app = appState.data?.app
  const isArchivedApp = isDeleted(app)
  const isAppBlocking = !appState.data && (appState.isLoading || isDefined(appState.error))

  const allVersions = useMemo(() => sortVersions(appState.data?.versions ?? []), [appState.data?.versions])
  const draftVersion = useMemo(() => allVersions.find(isDraftVersion), [allVersions])
  const publishedVersions = useMemo(() => allVersions.filter(isPublishedVersion), [allVersions])
  const latestPublished = publishedVersions[0]
  const extraDrafts = useMemo(() => allVersions.filter(isDraftVersion).slice(1), [allVersions])
  const currentVersion = useMemo(
    () => allVersions.find((version) => version.appVersion === app?.currentVersion),
    [allVersions, app?.currentVersion]
  )

  const viewedVersion = useMemo<AppVersionData | undefined>(() => {
    if (isDefined(urlVersion)) return allVersions.find((v) => v.appVersion === urlVersion)
    return draftVersion ?? latestPublished
  }, [urlVersion, allVersions, draftVersion, latestPublished])
  const isViewingDraft = isDefined(draftVersion) && viewedVersion?.appVersion === draftVersion.appVersion

  const headerAction = useMemo(
    () =>
      isNotDefined(draftVersion) && (
        <ButtonAdd onClick={openStartDraft}>
          {isDefined(latestPublished) ? t('Start new draft') : t('Create version')}
        </ButtonAdd>
      ),
    [draftVersion, latestPublished, openStartDraft, t]
  )

  function handleViewPublished(target: AppVersionData): void {
    confirmLeave(() => {
      void setUrlVersion(target.appVersion)
    })
  }

  function handleReturnToDraft(): void {
    confirmLeave(() => {
      void setUrlVersion(null)
    })
  }

  return (
    <ConnectAccessGuard>
      <Page>
        <Page.Heading
          back
          handleGoBack={() => navigate({ to: CONNECT_DEVELOPER_HREF })}
          isFetching={appState.isFetching}
          leftSection={
            app && (
              <ConnectAppLogo avatarBlurhash={app.avatarBlurhash} avatarUrl={app.avatarUrl} name={app.name} size='sm' />
            )
          }
          refetch={appState.refetch}
          title={app?.name || t('Connect App')}
          breadcrumbs={[
            { href: CONNECT_DEVELOPER_HREF, label: t('Developer') },
            { disabled: true, href: '#', label: app?.name || t('App') },
          ]}
        >
          {headerAction}
        </Page.Heading>
        <Page.Content scrollArea>
          <Stack gap='md'>
            {isAppBlocking && (
              <NotReady
                data={appState.data}
                error={appState.error}
                id={t('connect app')}
                isLoading={appState.isLoading}
              />
            )}
            {appState.data && appState.error && (
              <TAlert variant='warning'>{t('Refresh failed, using cached data.')}</TAlert>
            )}
            {isArchivedApp && <TAlert>{t('This app is archived and cannot be modified.')}</TAlert>}
            {isNotEmpty(extraDrafts) && (
              <TAlert variant='warning'>
                {t(
                  'Multiple drafts detected. Editing the latest only — archive older drafts from the previously published list to clean up.'
                )}
              </TAlert>
            )}
            {appState.data && (
              <>
                <div>
                  <TSegmentedControl
                    value={section}
                    onChange={(nextSection): void => {
                      confirmLeave(() => {
                        void setSection(nextSection)
                      })
                    }}
                    options={segmentedControlOptions}
                  />
                </div>

                {section === 'details' && isDefined(app) && (
                  <AppDetailsTab app={app} currentVersion={currentVersion} registerDirtyCheck={registerDirtyCheck} />
                )}

                {section === 'config' && (
                  <ConfigTabBody
                    appId={appId}
                    app={app}
                    draftVersion={draftVersion}
                    latestPublished={latestPublished}
                    publishedVersions={publishedVersions}
                    viewedVersion={viewedVersion}
                    isViewingDraft={isViewingDraft}
                    onCreate={openStartDraft}
                    onViewPublished={handleViewPublished}
                    onReturnToDraft={handleReturnToDraft}
                    registerDirtyCheck={registerDirtyCheck}
                  />
                )}

                {section === 'monitoring' && (
                  <MonitoringTab appId={appId} versions={allVersions} currentVersion={app?.currentVersion} />
                )}
              </>
            )}
          </Stack>
        </Page.Content>
      </Page>
      <StartDraftModal
        appId={appId}
        opened={startDraftOpened}
        onClose={closeStartDraft}
        onCreated={() => {
          setUrlVersion(null)
          setSection('config')
        }}
        template={latestPublished}
      />
    </ConnectAccessGuard>
  )
}

export const Route = createFileRoute('/integration/developer_/$appId/')({
  validateSearch: (search: Record<string, unknown>) => ({
    section: isConnectEditorSection(search.section) ? search.section : undefined,
    version: typeof search.version === 'string' ? search.version : undefined,
  }),
  component: ConnectAppPage,
})
